import React, { useMemo, useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';

/**
 * TinyMCE Cloud editor for product descriptions.
 *
 * Requires REACT_APP_TINYMCE_API_KEY at client build time (Render env + redeploy).
 * Server CSP must allow https://cdn.tiny.cloud (already in helmet).
 */
const apiKey = (process.env.REACT_APP_TINYMCE_API_KEY || '').trim();

export default function ProductDescriptionEditor({ value, onChange, disabled = false, height = 420 }) {
  const editorRef = useRef(null);

  const init = useMemo(
    () => ({
      height,
      menubar: 'file edit view insert format tools table help',
      branding: false,
      promotion: false,
      statusbar: true,
      resize: true,
      plugins: [
        'advlist',
        'anchor',
        'autolink',
        'charmap',
        'code',
        'codesample',
        'directionality',
        'emoticons',
        'fullscreen',
        'help',
        'image',
        'insertdatetime',
        'link',
        'lists',
        'media',
        'preview',
        'searchreplace',
        'table',
        'visualblocks',
        'visualchars',
        'wordcount',
      ],
      toolbar: [
        'undo redo | blocks | fontfamily fontsize | bold italic underline strikethrough | forecolor backcolor',
        'alignleft aligncenter alignright alignjustify | lineheight | bullist numlist | outdent indent | blockquote',
        'link image media table | charmap emoticons | removeformat | searchreplace | code preview fullscreen',
      ],
      toolbar_mode: 'wrap',
      font_family_formats:
        'Andale Mono=andale mono,times;' +
        'Arial=arial,helvetica,sans-serif;' +
        'Arial Black=arial black,avant garde;' +
        'Book Antiqua=book antiqua,palatino;' +
        'Comic Sans MS=comic sans ms,sans-serif;' +
        'Courier New=courier new,courier;' +
        'Georgia=georgia,palatino;' +
        'Helvetica=helvetica;' +
        'Impact=impact,chicago;' +
        'Symbol=symbol;' +
        'Tahoma=tahoma,arial,helvetica,sans-serif;' +
        'Terminal=terminal,monaco;' +
        'Times New Roman=times new roman,times;' +
        'Trebuchet MS=trebuchet ms,geneva;' +
        'Verdana=verdana,geneva;' +
        'Webdings=webdings;' +
        'Wingdings=wingdings,zapf dingbats',
      font_size_formats: '10pt 12pt 14pt 16pt 18pt',
      block_formats: 'Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4; Preformatted=pre',
      advlist_bullet_styles: 'default,circle,square',
      advlist_number_styles: 'default,lower-alpha,lower-roman,upper-alpha,upper-roman',
      content_style:
        'body { font-family: Arial, Helvetica, sans-serif; font-size: 14pt; line-height: 1.5; }' +
        'li::marker { color: #d52b1e; }',
      convert_urls: false,
      relative_urls: false,
      image_title: true,
      automatic_uploads: false,
      file_picker_types: 'image',
    }),
    [height]
  );

  if (!apiKey) {
    return (
      <textarea
        value={value || ''}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder="Product description"
        style={{
          width: '100%',
          minHeight: height,
          boxSizing: 'border-box',
          padding: '0.75rem',
          border: '1px solid #d1d5db',
          borderRadius: 6,
          fontSize: '0.875rem',
          fontFamily: 'inherit',
          lineHeight: 1.45,
          resize: 'vertical',
        }}
      />
    );
  }

  return (
    <Editor
      apiKey={apiKey}
      onInit={(_evt, editor) => {
        editorRef.current = editor;
      }}
      value={value || ''}
      disabled={disabled}
      onEditorChange={(content) => onChange?.(content)}
      init={init}
    />
  );
}
